import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, DollarSign, TrendingUp, AlertTriangle, Eye } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default marker icons in Leaflet with Webpack
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// US center coordinates
const US_CENTER = [39.8, -98.5];
const DEFAULT_ZOOM = 4;

// Color scale based on competitiveness score
const getMarkerColor = (score) => {
  if (score >= 70) return '#229644'; // Green - healthy
  if (score >= 50) return '#FFA500'; // Orange - at risk
  return '#FF521C'; // Red - critical
};

// Marker size based on contract value
const getMarkerSize = (value) => {
  if (value >= 1000000) return 12;
  if (value >= 500000) return 10;
  if (value >= 200000) return 8;
  return 6;
};

// Heat intensity calculation
const getHeatIntensity = (contract) => {
  const { competitivenessScore, annualValue, daysToExpiration } = contract;
  const urgencyWeight = Math.exp(-daysToExpiration / 90); // Exponential decay
  const riskWeight = 1 - (competitivenessScore / 100);
  return annualValue * urgencyWeight * riskWeight;
};

// Component to fit bounds when data loads
function FitBoundsOnLoad({ features }) {
  const map = useMap();
  
  useEffect(() => {
    if (features && features.length > 0) {
      const bounds = features.map(f => [
        f.geometry.coordinates[1],
        f.geometry.coordinates[0]
      ]);
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 6 });
      }
    }
  }, [features, map]);
  
  return null;
}

// Region summary stats
function RegionStats({ features }) {
  if (!features || features.length === 0) return null;
  
  const stats = features.reduce((acc, f) => {
    const props = f.properties;
    acc.totalValue += props.annualValue;
    acc.totalContracts += 1;
    if (props.competitivenessScore < 50) acc.atRisk += 1;
    if (props.daysToExpiration <= 30) acc.expiringSoon += 1;
    return acc;
  }, { totalValue: 0, totalContracts: 0, atRisk: 0, expiringSoon: 0 });
  
  return (
    <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 text-xs">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <div className="flex items-center gap-1">
          <MapPin className="w-3 h-3 text-[#09409C]" />
          <span className="font-medium">{stats.totalContracts}</span>
          <span className="text-muted-foreground">contracts</span>
        </div>
        <div className="flex items-center gap-1">
          <DollarSign className="w-3 h-3 text-[#229644]" />
          <span className="font-medium">{formatCurrency(stats.totalValue)}</span>
        </div>
        <div className="flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 text-red-500" />
          <span className="font-medium">{stats.atRisk}</span>
          <span className="text-muted-foreground">at risk</span>
        </div>
        <div className="flex items-center gap-1">
          <TrendingUp className="w-3 h-3 text-orange-500" />
          <span className="font-medium">{stats.expiringSoon}</span>
          <span className="text-muted-foreground">expiring &lt;30d</span>
        </div>
      </div>
    </div>
  );
}

// Contract popup content
function ContractPopup({ properties, onViewDetails }) {
  return (
    <div className="min-w-[220px] p-1">
      <div className="font-semibold text-sm mb-1">{properties.accountName}</div>
      <div className="text-xs text-muted-foreground mb-2">{properties.contractNumber}</div>
      
      <div className="space-y-1.5">
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Annual Value:</span>
          <span className="font-medium">{formatCurrency(properties.annualValue)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Expires in:</span>
          <Badge 
            className={`text-[10px] py-0 px-1.5 ${
              properties.daysToExpiration <= 30 ? 'bg-red-100 text-red-800' : 
              properties.daysToExpiration <= 60 ? 'bg-yellow-100 text-yellow-800' : 
              'bg-green-100 text-green-800'
            }`}
          >
            {properties.daysToExpiration} days
          </Badge>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Competitiveness:</span>
          <Badge 
            className={`text-[10px] py-0 px-1.5 ${
              properties.competitivenessScore >= 70 ? 'bg-green-100 text-green-800' : 
              properties.competitivenessScore >= 50 ? 'bg-yellow-100 text-yellow-800' : 
              'bg-red-100 text-red-800'
            }`}
          >
            {properties.competitivenessScore}%
          </Badge>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Location:</span>
          <span>{properties.city}, {properties.state}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">ISO Region:</span>
          <span className="font-medium">{properties.isoRegion || 'N/A'}</span>
        </div>
      </div>
      
      {onViewDetails && (
        <Button 
          size="sm" 
          className="w-full mt-3 h-7 text-xs bg-[#09409C] hover:bg-[#146CBB]"
          onClick={() => onViewDetails(properties.contractId)}
        >
          <Eye className="w-3 h-3 mr-1" />
          View Details
        </Button>
      )}
    </div>
  );
}

// Legend component
function MapLegend() {
  return (
    <div className="absolute bottom-4 right-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 text-xs">
      <div className="font-medium mb-2">Contract Health</div>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#229644]" />
          <span>Healthy (70%+)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#FFA500]" />
          <span>At Risk (50-69%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#FF521C]" />
          <span>Critical (&lt;50%)</span>
        </div>
      </div>
    </div>
  );
}

export default function RenewalMap({ 
  height = '400px', 
  showLegend = true, 
  showStats = true,
  onViewDetails,
  interactive = true,
  className = ''
}) {
  const [selectedContract, setSelectedContract] = useState(null);
  
  const { data: geoData, isLoading, error } = useQuery({
    queryKey: ['geo-contracts'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/geo/contracts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 rounded-lg ${className}`} style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#09409C]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 rounded-lg ${className}`} style={{ height }}>
        <div className="text-center text-muted-foreground">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-400" />
          <p>Failed to load map data</p>
        </div>
      </div>
    );
  }

  const features = geoData?.features || [];

  return (
    <div className={`relative rounded-lg overflow-hidden ${className}`} style={{ height }} data-testid="renewal-map">
      <MapContainer
        center={US_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={interactive}
        dragging={interactive}
        doubleClickZoom={interactive}
        zoomControl={interactive}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <FitBoundsOnLoad features={features} />
        
        {features.map((feature, idx) => {
          const [lng, lat] = feature.geometry.coordinates;
          const props = feature.properties;
          const color = getMarkerColor(props.competitivenessScore);
          const size = getMarkerSize(props.annualValue);
          const intensity = getHeatIntensity(props);
          
          return (
            <CircleMarker
              key={props.contractId || idx}
              center={[lat, lng]}
              radius={size}
              pathOptions={{
                color: color,
                fillColor: color,
                fillOpacity: 0.7,
                weight: 2,
              }}
              eventHandlers={{
                click: () => setSelectedContract(props),
              }}
            >
              <Popup>
                <ContractPopup 
                  properties={props} 
                  onViewDetails={onViewDetails}
                />
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
      
      {showStats && <RegionStats features={features} />}
      {showLegend && <MapLegend />}
    </div>
  );
}
